import { Card, IconButton, LuIcon, Menu, Spinner, stopPropagation } from "@overmap-ai/blocks"
import { ImageFile, useImageViewer } from "@overmap-ai/forms"
import type { NodeViewProps } from "@tiptap/react"
import { cx } from "class-variance-authority"
import { saveAs } from "file-saver"
import { memo, useCallback } from "react"

import { modelIconMapping } from "../../../mappings/icons"
import { useEditorEditable } from "../../hooks"

interface ImageFileNodeComponentProps extends NodeViewProps {
	file: File | null
	error?: string
}

export const ImageFileNodeComponent = memo((props: ImageFileNodeComponentProps) => {
	const {
		file,
		error,
		// ReactNodeViewProps
		editor,
		selected,
		deleteNode,
	} = props

	const openImageViewer = useImageViewer()
	const isEditable = useEditorEditable(editor)

	const handleDelete = useCallback(() => {
		deleteNode()
		editor.chain().focus().run()
	}, [deleteNode, editor])

	const handleExpandClick = useCallback(() => {
		if (!file) return
		openImageViewer((close) => ({
			file: file,
			onDelete: () => {
				handleDelete()
				close()
			},
		}))
	}, [file, handleDelete, openImageViewer])

	const handleDownload = useCallback(() => {
		if (!file) return

		saveAs(file, file.name)
	}, [file])

	return (
		<Card
			className={cx("p-0! flex w-max overflow-hidden", {
				"outline-(--primary-indicator) outline-2 -outline-offset-1":
					selected && editor.isEditable && editor.isFocused,
			})}
			size="sm"
		>
			<div className="relative flex">
				{!error ? (
					file ? (
						<ImageFile className="max-h-[450px] max-w-full select-none object-contain" file={file} />
					) : (
						<Spinner />
					)
				) : (
					<LuIcon icon={modelIconMapping.alertCircle} />
				)}
				{!error && !!file && (
					<Card className="bg-(--color-background) p-0! absolute bottom-2 right-2" variant="soft" size="sm">
						<Menu.Root>
							<Menu.ClickTrigger>
								<IconButton
									variant="ghost"
									size="sm"
									accentColor="base"
									type="button"
									onClick={stopPropagation}
								>
									<LuIcon icon={modelIconMapping.more} />
								</IconButton>
							</Menu.ClickTrigger>
							<Menu.Content size="sm">
								<Menu.Item onSelect={handleExpandClick} disabled={!file}>
									<LuIcon icon={modelIconMapping.expand} />
									View
								</Menu.Item>

								<Menu.Item onSelect={handleDownload} disabled={!file}>
									<LuIcon icon={modelIconMapping.download} />
									Download
								</Menu.Item>

								{isEditable && (
									<>
										<Menu.Separator />
										<Menu.Item onSelect={handleDelete} disabled={!file}>
											<LuIcon icon={modelIconMapping.delete} />
											Delete
										</Menu.Item>
									</>
								)}
							</Menu.Content>
						</Menu.Root>
					</Card>
				)}
			</div>
		</Card>
	)
})
ImageFileNodeComponent.displayName = "ImageCell"
